import Head from 'next/head'
import Script from 'next/script'
import Image from 'next/image'
import { s3Static } from '../../../utils/constants'
import { BlogsList } from '../../../components/blogs/BlogList'
import { blogsMaster } from '../../../utils/sg-blogs-data'
import { getRequestJSON } from '../../../api/index/location-api'
import { useState } from 'react'
import { RequestProvider } from '../../../context/RequestContext'
import AirconRequestSection from '../../../components/index/service-category/aircon-service/AirconRequestSection'
import { AirconScope } from '../../../components/index/service-category/aircon-service/AirconScope'
import { AnimatePresence } from 'framer-motion'
import Link from 'next/link'
import { AirconServiceCostCalculator } from '../../../components/index/service-category/aircon-service/AirconServiceCostCalculator'
import { TRANSPORT } from '../../../components/index/service-category/aircon-service/AirconPriceComparison'
import { airconCostCalculation } from '../../../js/aircon-cost'
import { airconDeals } from '../../../utils/airconDeals'
import { localBusinessNode, WEBSITE_ID } from '../../../utils/local-business-schema'
import { buildBreadcrumb, breadcrumbJsonLd } from '../../../utils/breadcrumb'
import { buildAirconPriceFaqs } from '../../../utils/faq'
import { Breadcrumb } from '../../../components/commons/Breadcrumb'
import { Faq } from '../../../components/commons/Faq'
import dynamic from 'next/dynamic'
import {
    ArrowRight,
    BadgePercent,
    Building2,
    Calculator,
    CircleCheck,
    Database,
    MapPin,
    Receipt,
    ShieldCheck,
    Snowflake,
    Tag,
    TriangleAlert,
    Wrench,
} from 'lucide-react'
import AirconPricing from '@/index/service-category/aircon-service/AirconPricing'

// Recharts renders nothing on the server (ResponsiveContainer needs a measured width),
// so loading it client-side costs no crawlable content and keeps it out of the initial
// bundle. The fixed-height placeholder reserves the space so nothing shifts on load.
const chartPlaceholder = () => <div className='h-full w-full rounded-lg bg-gray-100' />
const CostDistributionChart = dynamic(() => import('../../../components/index/service-category/aircon-service/AirconPriceCharts').then((m) => m.CostDistributionChart), { ssr: false, loading: chartPlaceholder })
const DemandChart = dynamic(() => import('../../../components/index/service-category/aircon-service/AirconPriceCharts').then((m) => m.DemandChart), { ssr: false, loading: chartPlaceholder })
const PriceTrendChart = dynamic(() => import('../../../components/index/service-category/aircon-service/AirconPriceCharts').then((m) => m.PriceTrendChart), { ssr: false, loading: chartPlaceholder })

// Aircon price data points in the StringsSG database (service requests and quotes
// since 2018). Update from the database when the charts below are refreshed.
const DATA_POINTS = '19,287'

// The date these prices were last checked. It drives the visible "Prices reviewed"
// line, the year in the <title> and schema dateModified. It used to be new Date()
// at build time, which moved "Updated" forward on every 3-day ISR regeneration
// whether or not anything had changed - bump this by hand when prices are reviewed.
const PRICES_REVIEWED = '2026-09-26'

// Published rates of Singapore aircon companies, checked on PRICES_REVIEWED
// (coolaircon.sg, 338aircon.sg, skyblueaircon.com). The 3-unit general range is
// StringsSG's own booking research. Most providers add a transport fee on top.
const marketRanges = {
    general1: '$30 to $55',
    general3: '$75 to $100',
    wash: '$80 to $120',
    overhaul: '$130 to $180',
    gas: '$40 to $220',
    condenser: '$50 to $80',
    condenserChemical: '$100 to $150',
}

// Typical market top-up prices by refrigerant, shown in AirconPricing's gas table.
const gasTopup = [
    { type: 'R22', priceRange: 'S$40 - S$100' },
    { type: 'R410A', priceRange: 'S$90 - S$150' },
    { type: 'R32', priceRange: 'S$120 - S$220' },
]

const pageUrl = "https://www.stringssg.com/sg/aircon-servicing-singapore/aircon-servicing-price-singapore"
const pagePath = "/sg/aircon-servicing-singapore/aircon-servicing-price-singapore"
const pageImage = "https://stringssg-assets.s3.ap-southeast-1.amazonaws.com/static/aircon/aircon-service-price-2026.png"
const breadcrumbLabel = "Aircon Servicing Price"

const money = (n) => `$${Number(n).toFixed(2)}`

const monthNames = { Jan: 'January', Feb: 'February', Mar: 'March', Apr: 'April', May: 'May', Jun: 'June', Jul: 'July', Aug: 'August', Sep: 'September', Oct: 'October', Nov: 'November', Dec: 'December' }

// "81-90" -> "$81 to $90", "100+" -> "over $100"
const bucketLabel = (range) => range.endsWith('+') ? `over $${parseInt(range, 10)}` : `$${range.replace('-', ' to $')}`

const inlineLink = 'text-orange-600 font-semibold underline decoration-orange-200 underline-offset-2 hover:decoration-orange-600'

// Presentational building blocks. Plain markup and CSS only - no motion - so every
// section is fully rendered in the static HTML and costs nothing at runtime.
// scroll-mt-20 clears the fixed 56px site header when a section is reached by an
// "On this page" jump link.
const Section = ({ id, eyebrow, title, intro, tinted = false, children }) => (
    <section id={id} aria-labelledby={`${id}-title`} className={`scroll-mt-20 py-12 md:py-16 ${tinted ? 'bg-gray-50' : 'bg-white'}`}>
        <div className='max-w-5xl mx-auto px-4 space-y-6'>
            <header className='max-w-3xl space-y-2'>
                {eyebrow && <p className='text-xs font-semibold uppercase tracking-wider text-orange-600'>{eyebrow}</p>}
                <h2 id={`${id}-title`} className='text-2xl md:text-3xl font-bold tracking-tight text-gray-900'>{title}</h2>
                {intro && <p className='text-gray-600 leading-7'>{intro}</p>}
            </header>
            {children}
        </div>
    </section>
)

// First cell of each row is rendered as the row header. `highlight` is the index of
// the column carrying StringsSG's price.
const DataTable = ({ caption, columns, rows, highlight }) => (
    <div className='overflow-x-auto rounded-2xl border border-gray-200 bg-white shadow-sm'>
        <table className='w-full text-sm text-left'>
            <caption className='sr-only'>{caption}</caption>
            <thead className='bg-gray-50 text-xs uppercase tracking-wide text-gray-500'>
                <tr>
                    {columns.map((col) => <th key={col} scope='col' className='px-3 md:px-5 py-3 font-semibold'>{col}</th>)}
                </tr>
            </thead>
            <tbody className='divide-y divide-gray-100'>
                {rows.map(([head, ...cells], r) => (
                    <tr key={r}>
                        <th scope='row' className='px-3 md:px-5 py-3 font-medium text-gray-900'>{head}</th>
                        {cells.map((cell, c) => (
                            <td key={c} className={`px-3 md:px-5 py-3 ${c + 1 === highlight ? 'whitespace-nowrap font-semibold text-orange-600' : 'text-gray-600'}`}>{cell ?? '-'}</td>
                        ))}
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
)

const StatCard = ({ value, label }) => (
    <div className='rounded-2xl border border-gray-200 bg-white p-4 shadow-sm'>
        <p className='text-2xl md:text-3xl font-bold tracking-tight text-gray-900'>{value}</p>
        <p className='mt-1 text-xs md:text-sm text-gray-500 leading-5'>{label}</p>
    </div>
)

const ChartCard = ({ title, children, takeaway }) => (
    <figure className='rounded-2xl border border-gray-200 bg-white p-4 md:p-5 shadow-sm space-y-3'>
        <h3 className='text-base font-semibold text-gray-900'>{title}</h3>
        <div className='h-64'>{children}</div>
        <figcaption className='text-sm text-gray-600 leading-6'>{takeaway}</figcaption>
    </figure>
)

const IconCard = ({ icon: Icon, title, children }) => (
    <div className='rounded-2xl border border-gray-200 bg-white p-5 shadow-sm space-y-2'>
        <div className='flex items-center gap-3'>
            <span className='flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-orange-50 text-orange-600'>
                <Icon className='h-5 w-5' aria-hidden='true' />
            </span>
            <h3 className='font-semibold text-gray-900'>{title}</h3>
        </div>
        <div className='text-sm text-gray-600 leading-6'>{children}</div>
    </div>
)

export async function getStaticProps() {
    const countryName = "Singapore"

    const allRequestCategorySettings = await getRequestJSON(countryName)

    const requestCategorySettings = allRequestCategorySettings.filter(category => category.requestType === "Aircon Services")

    const airconBlogs = blogsMaster['hvac-services'].blogArray

    const currencyCd = "SGD"

    let standardServicing = []
    let annualServicing = []
    let chemicalServicing = []
    let overhaulServicing = []

    airconCostCalculation(currencyCd, annualServicing, standardServicing, chemicalServicing, overhaulServicing)

    // Every price quoted in the copy, the FAQ and the schema is derived here from the
    // same arrays the price tables render, so the prose cannot drift from the tables.
    const generalTiers = standardServicing.map((row, i) => {
        const units = i + 1
        const total = Number(row.rate)
        return { units, total, perUnit: total / units, effective: (total - TRANSPORT) / units }
    })
    const best = generalTiers.reduce((a, b) => (b.effective < a.effective ? b : a))

    const rate = (rows, units) => Number(rows[units - 1].rate)
    const dealCost = (serviceType, unit = 1) =>
        airconDeals.find((d) => d.serviceType === serviceType && d.unit === unit)?.cost ?? null

    // annualServicing's keys are swapped relative to their names (see js/aircon-cost.js):
    // threeMonthInterval holds the 4-visits-a-year price, fourMonthInterval the 3-visit one.
    const annual4x3 = Number(annualServicing[2].threeMonthInterval)
    const annual3x3 = Number(annualServicing[2].fourMonthInterval)
    const oneOff4x3 = rate(standardServicing, 3) * 4

    const priceMap = {
        'general-1': money(rate(standardServicing, 1)),
        'general-3': money(rate(standardServicing, 3)),
        'general-5': money(rate(standardServicing, 5)),
        'perUnit-3': money(generalTiers[2].perUnit),
        'perUnit-5': money(generalTiers[4].perUnit),
        'effective-5': money(best.effective),
        'effective-headline': `$${Math.floor(best.effective)}`,
        'transport': `$${TRANSPORT}`,
        'wash-1': money(rate(chemicalServicing, 1)),
        'overhaul-1': money(rate(overhaulServicing, 1)),
        'annual4x-3': money(annual4x3),
        'annual3x-3': money(annual3x3),
        'oneoff4x-3': money(oneOff4x3),
        'annualSaving-3': money(oneOff4x3 - annual4x3),
        'gas-half': dealCost('gasRefillHalf') && money(dealCost('gasRefillHalf')),
        'gas-full': dealCost('gasRefill') && money(dealCost('gasRefill')),
        'market-general-1': marketRanges.general1,
        'market-general-3': marketRanges.general3,
        'market-wash': marketRanges.wash,
        'market-overhaul': marketRanges.overhaul,
        'market-gas': marketRanges.gas,
    }

    const otherPrices = [
        { service: 'Ceiling cassette general service (1 unit)', price: dealCost('generalCeiling'), href: '/sg/aircon-servicing-singapore/ceiling-cassette-aircon-service' },
        { service: 'Gas top-up, half tank (1 unit)', price: dealCost('gasRefillHalf'), href: '/sg/aircon-servicing-singapore/aircon-gas-top-up' },
        { service: 'Gas top-up, full tank (1 unit)', price: dealCost('gasRefill'), href: '/sg/aircon-servicing-singapore/aircon-gas-top-up' },
        { service: 'Condenser general cleaning (1 outdoor unit)', price: dealCost('compressorCleaning'), market: marketRanges.condenser, href: '/sg/aircon-servicing-singapore/aircon-condenser-cleaning' },
        { service: 'Condenser chemical cleaning (1 outdoor unit)', price: dealCost('compressorChemicalCleaning'), market: marketRanges.condenserChemical, href: '/sg/aircon-servicing-singapore/aircon-condenser-cleaning' },
    ].filter((row) => row.price != null)

  return {
      props: {
        requestCategorySettings,
        airconBlogs,
        standardServicing,
        annualServicing,
        chemicalServicing,
        overhaulServicing,
        currencyCd,
        generalTiers,
        priceMap,
        otherPrices,
      },
      // Next.js will attempt to re-generate the page:
      // - When a request comes in
      // - At most once every 3 days
      revalidate: 259200, // In seconds
  }
}
export default function Home(props) {
    const countryName = "Singapore"
    const requestType = "Aircon Services"

    const [ requestForm, setRequestForm ] = useState(false)
    const [ isQuickBook, setIsQuickBook ] = useState(false)
    const [ isCustomBook, setIsCustomBook ] = useState(false)
    const [ isShowJobScope, setIsShowJobScope ] = useState(false)
    const [ serviceType, setServiceType ] = useState("general")
    const [ unit, setUnit ] = useState("")
    const [ unitType, setUnitType ] = useState("")
    const [ cost, setCost ] = useState("")
    const airconBlogs = props.airconBlogs
    const requestCategorySettings = props.requestCategorySettings;
    const chemicalServicing = props.chemicalServicing
    const overhaulServicing = props.overhaulServicing
    const standardServicing = props.standardServicing
    const annualServicing = props.annualServicing
    const currencyCd = props.currencyCd
    const generalTiers = props.generalTiers
    const priceMap = props.priceMap
    const otherPrices = props.otherPrices

    const p = (key) => priceMap[key] ?? null
    const faqs = buildAirconPriceFaqs(p)

    // Formatted by hand rather than with toLocaleDateString, whose output can differ
    // between the build server's ICU data and the browser and break hydration.
    const [ reviewedYear, reviewedMonth, reviewedDay ] = PRICES_REVIEWED.split('-')
    const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"]
    const reviewedIso = `${PRICES_REVIEWED}T09:00:00+08:00`
    const reviewedLabel = `${Number(reviewedDay)} ${months[Number(reviewedMonth) - 1]} ${reviewedYear}`

    // Title, description and the schema all read from these, so a price change
    // lands everywhere at once.
    const pageTitle = `Aircon Servicing Price Singapore ${reviewedYear} | From ${p('effective-headline')}/Unit`
    const pageDescription = `${reviewedYear} aircon servicing prices in Singapore: 1 unit ${p('general-1')}, 3 units ${p('general-3')}, 5 units ${p('general-5')}, transport included. Chemical wash from ${p('wash-1')}, overhaul from ${p('overhaul-1')}.`

    const glance = [
        { service: 'General servicing, 1 unit', price: p('general-1'), market: marketRanges.general1 },
        { service: 'General servicing, 3 units', price: p('general-3'), market: marketRanges.general3 },
        { service: 'General servicing, 5 units', price: p('general-5'), market: null },
        { service: 'Chemical wash, per unit', price: p('wash-1'), market: marketRanges.wash },
        { service: 'Chemical overhaul, per unit', price: p('overhaul-1'), market: marketRanges.overhaul },
        { service: 'Gas top-up, full tank', price: p('gas-full'), market: `${marketRanges.gas} (by refrigerant)` },
        { service: 'Annual package, 3 units x 4 visits', price: p('annual4x-3'), market: null },
    ].filter((row) => row.price)

    const offer = (name, service, price) => ({
        "@type": "Offer",
        name,
        priceCurrency: "SGD",
        price: Number(price).toFixed(2),
        priceSpecification: { "@type": "PriceSpecification", priceCurrency: "SGD", price: Number(price).toFixed(2), valueAddedTaxIncluded: true },
        itemOffered: { "@type": "Service", name: service },
    })
    const unitLabel = (n) => `${n} ${n === 1 ? 'unit' : 'units'}`

    const schema = {
        "@context": "https://schema.org",
        "@graph": [
            {
                "@type": "WebPage",
                "@id": `${pageUrl}/#webpage`,
                url: pageUrl,
                name: pageTitle,
                description: pageDescription,
                inLanguage: "en-SG",
                primaryImageOfPage: { "@type": "ImageObject", url: pageImage },
                datePublished: "2025-01-29T07:30:46+00:00",
                dateModified: reviewedIso,
                isPartOf: { "@type": "WebSite", "@id": WEBSITE_ID, url: "https://www.stringssg.com/", name: "StringsSG" },
                breadcrumb: { "@id": `${pageUrl}/#breadcrumblist` },
            },
            breadcrumbJsonLd(buildBreadcrumb(pagePath, breadcrumbLabel), pageUrl),
            {
                "@type": "Service",
                "@id": `${pageUrl}/#service`,
                name: "Aircon Servicing Singapore",
                serviceType: "Aircon Servicing",
                description: pageDescription,
                url: pageUrl,
                image: pageImage,
                provider: localBusinessNode,
                areaServed: { "@type": "Country", name: "Singapore" },
                hasOfferCatalog: {
                    "@type": "OfferCatalog",
                    name: "Aircon Servicing Prices in Singapore",
                    itemListElement: [
                        ...standardServicing.map((row, i) => offer(`General Aircon Servicing - ${unitLabel(i + 1)}`, "General Aircon Servicing", row.rate)),
                        ...chemicalServicing.map((row, i) => offer(`Aircon Chemical Wash - ${unitLabel(i + 1)}`, "Aircon Chemical Wash", row.rate)),
                        ...overhaulServicing.map((row, i) => offer(`Aircon Chemical Overhaul - ${unitLabel(i + 1)}`, "Aircon Chemical Overhaul", row.rate)),
                        ...otherPrices.map((row) => offer(row.service, row.service.replace(/ \(.*\)$/, ''), row.price)),
                    ],
                },
            },
        ],
    }

    const costData = [
    { range: '65-75', cost: 20 },
    { range: '76-84', cost: 30 },
    { range: '85-95', cost: 35 },
    { range: '96-104', cost: 10 },
    { range: '104+', cost: 5 },
    ];

    const demandData = [
    { month: 'Jan-22', demand: 40 },
    { month: 'Mar-22', demand: 42 },
    { month: 'Jul-22', demand: 80 },
    { month: 'Sep-22', demand: 55 },
    { month: 'Jan-23', demand: 30 },
    { month: 'Mar-23', demand: 70 },
    { month: 'Jul-23', demand: 90 },
    { month: 'Sep-23', demand: 50 },
    { month: 'Jan-24', demand: 45 },
    { month: 'Mar-24', demand: 75 },
    { month: 'Jul-24', demand: 100 },
    { month: 'Sep-24', demand: 60 },
    { month: 'Jan-25', demand: 55 },
    { month: 'Mar-25', demand: 85 },
    { month: 'Jul-25', demand: 100 },
    { month: 'Sep-25', demand: 70 },
    { month: 'Jan-26', demand: 58 },
    { month: 'Mar-26', demand: 88 },
    { month: 'Jul-26', demand: 115 },
    { month: 'Sep-26', demand: 72 },
    ];

    const priceTrendData = [
    { year: '2015', price: 35 },
    { year: '2020', price: 36 },
    { year: '2024', price: 41 },
    { year: '2026', price: 50 },
    ];

    // Takeaways written into the copy are derived from the chart data above, so
    // refreshing an array updates the prose that describes it.
    const peakBucket = costData.reduce((a, b) => (b.cost > a.cost ? b : a))
    const shareAbove90 = costData.filter((b) => parseInt(b.range, 10) > 90).reduce((sum, b) => sum + b.cost, 0)

    const demandByYear = demandData.reduce((acc, d) => {
        const [ m, y ] = d.month.split('-')
        ;(acc[y] = acc[y] || []).push({ m, v: d.demand })
        return acc
    }, {})
    // The month that is most often the high (or low) point across the years charted.
    const typicalMonth = (pick) => {
        const counts = {}
        Object.values(demandByYear).forEach((pts) => {
            const m = pts.reduce(pick).m
            counts[m] = (counts[m] || 0) + 1
        })
        return Object.entries(counts).sort((a, b) => b[1] - a[1])[0][0]
    }
    const peakMonth = typicalMonth((a, b) => (b.v > a.v ? b : a))
    const lowMonth = typicalMonth((a, b) => (b.v < a.v ? b : a))
    const peakPoints = demandData.filter((d) => d.month.startsWith(`${peakMonth}-`))
    const demandFirstYear = `20${demandData[0].month.slice(-2)}`
    const demandLastYear = `20${demandData[demandData.length - 1].month.slice(-2)}`

    const trendFirst = priceTrendData[0]
    const trendLast = priceTrendData[priceTrendData.length - 1]
    const trendRise = Math.round(((trendLast.price - trendFirst.price) / trendFirst.price) * 100)
    const oneUnitPrice = Number(standardServicing[0].rate)

    const onThisPage = [
        [ 'price-list-at-a-glance', 'Price list' ],
        [ 'aircon-servicing-cost', 'Cost in Singapore' ],
        [ 'price-per-unit', 'Price per unit' ],
        [ 'full-price-list', 'Full price tables' ],
        [ 'worked-example', 'HDB example' ],
        [ 'wash-vs-overhaul', 'Wash vs overhaul' ],
        [ 'gas-top-up-price', 'Gas top-up' ],
        [ 'cost-calculator', 'Calculator' ],
        [ 'price-statistics', 'Price statistics' ],
        [ 'hidden-fees', 'Hidden fees' ],
        [ 'faq', 'FAQ' ],
    ]

  return (
    <>
      <Head>
        <title>{pageTitle}</title>
        <meta charSet="utf-8"/>
        <meta httpEquiv="X-UA-Compatible" content="IE=edge"/>
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="description" content={pageDescription}/>
        <meta name="author" content="StringsSG"/>
        <meta name="google-site-verification" content="JAjj5vAc8Xxrh4idRAchZhYR6rwxWPAk3vxMdx-dwVQ" />
        <meta property="og:locale" content="en_SG" />
        <meta property="og:url"   content={pageUrl} />
        <meta property="og:type"   content="website" />
        <meta property="og:title"    content={pageTitle}/>
        <meta property="og:description" content={pageDescription} />
        <meta property="article:publisher" content="https://www.facebook.com/StringsSG/" />
        <meta property="article:modified_time" content={reviewedIso} />
        <meta property="og:image"  content={pageImage} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image"/>
        <meta name="twitter:image" content={pageImage} />
        <meta name="twitter:site" content="@stringssg"/>
        <meta name="twitter:url" content={pageUrl}/>
        <meta name="twitter:title" content={pageTitle}/>
        <meta name="twitter:description" content={pageDescription}/>
        <link rel="icon" href="/favicon.ico" />
        <script type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
        />
        <link rel="canonical" href={pageUrl}/>
        </Head>
        <div className='text-gray-800'>
            {/* Hero H1 lives in AirconBooking (via AirconRequestSection). The subtitle is the
                short answer to the query, written to be quotable as a featured snippet. */}
            <RequestProvider>
                <AirconRequestSection
                    requestForm={requestForm}
                    setRequestForm={setRequestForm}
                    isQuickBook={isQuickBook}
                    setIsQuickBook={setIsQuickBook}
                    isCustomBook={isCustomBook}
                    setIsCustomBook={setIsCustomBook}
                    requestCategorySettings={requestCategorySettings}
                    standardServicing={standardServicing}
                    chemicalServicing={chemicalServicing}
                    overhaulServicing={overhaulServicing}
                    currencyCd={currencyCd}
                    countryName={countryName}
                    requestType={requestType}
                    setIsShowJobScope={setIsShowJobScope}
                    serviceType={serviceType}
                    setServiceType={setServiceType}
                    setUnit={setUnit}
                    setUnitType={setUnitType}
                    setCost={setCost}
                    heroImage="aircon/aircon-in-living-room.jpg"
                    title="Aircon Servicing Price Singapore"
                    subtitle={`General servicing costs ${p('general-1')} for 1 unit to ${p('general-5')} for 5 units, transport included - about ${p('effective-5')} per unit once the ${p('transport')} transport fee most providers add is set aside.`}
                />
            </RequestProvider>

            {/* BreadcrumbList is already in the page @graph above, built from the same trail. */}
            <Breadcrumb pageTitle={breadcrumbLabel} jsonLd={false} />
            <Section
                id='price-list-at-a-glance'
                eyebrow={<>Prices reviewed <time dateTime={reviewedIso}>{reviewedLabel}</time></>}
                title={`Aircon servicing price list Singapore (${reviewedYear}) at a glance`}
            >
                <div className='grid grid-cols-2 md:grid-cols-4 gap-3'>
                    <StatCard value={p('general-1')} label='General service, 1 unit' />
                    <StatCard value={p('general-3')} label='General service, 3 units' />
                    <StatCard value={p('effective-5')} label={`Per unit for 5 units, excl. ${p('transport')} transport`} />
                    <StatCard value={DATA_POINTS} label='Aircon price data points since 2018' />
                </div>
                <p className='max-w-3xl leading-8'>
                    <strong>Short answer:</strong> general aircon servicing in Singapore costs {p('general-1')} for 1 unit,{' '}
                    {p('general-3')} for 3 units and {p('general-5')} for 5 units with StringsSG, transport included. Most Singapore
                    companies charge {marketRanges.general3} for 3 units. A chemical wash starts at {p('wash-1')} per unit and a chemical
                    overhaul at {p('overhaul-1')}.
                </p>
                <DataTable
                    caption='Aircon servicing prices in Singapore: StringsSG compared with typical market prices'
                    columns={[ 'Service', 'StringsSG', 'Typical Singapore price' ]}
                    highlight={1}
                    rows={glance.map((row) => [ row.service, row.price, row.market ])}
                />
                <p className='text-xs text-gray-500 leading-6'>
                    StringsSG prices include transport and are the final amount you pay. Typical Singapore prices are ranges published
                    by local aircon companies, checked {reviewedLabel}; many add a transport fee of $10 to $30 on top.
                </p>
                <nav aria-label='On this page' className='rounded-2xl border border-gray-200 bg-gray-50 p-4'>
                    <p className='mb-3 text-xs font-semibold uppercase tracking-wider text-gray-500'>On this page</p>
                    <ul className='flex flex-wrap gap-2'>
                        {onThisPage.map(([ id, label ]) => (
                            <li key={id}>
                                <a href={`#${id}`} className='inline-block rounded-full border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-700 hover:border-orange-300 hover:text-orange-600'>
                                    {label}
                                </a>
                            </li>
                        ))}
                    </ul>
                </nav>
            </Section>

            <Section id='aircon-servicing-cost' tinted title='How much does aircon servicing cost in Singapore?'>
                <div className='grid md:grid-cols-2 gap-8 items-center'>
                    <div className='space-y-4 leading-8'>
                        <p>
                            A one-time general service for 3 aircon units costs <strong>{marketRanges.general3}</strong> in Singapore.
                            The spread comes from the provider you choose, where you live, and what the quote includes: some prices
                            already cover transport and GST, others add them at the end.
                        </p>
                        <p>
                            Across <strong>{DATA_POINTS} aircon price data points</strong> collected on StringsSG since 2018, the most
                            common price for 3 units is {bucketLabel(peakBucket.range)} ({peakBucket.cost}% of customers), and{' '}
                            {shareAbove90}% pay more than $90.
                        </p>
                        <p>
                            StringsSG general servicing for 3 units is <strong>{p('general-3')}</strong> with transport included, and
                            the price per unit keeps falling as you add units to the same visit.
                        </p>
                    </div>
                    <div className='relative h-64 md:h-80 w-full overflow-hidden rounded-2xl'>
                        <Image
                            src={s3Static + "blog/living/cool-air-from-aircon.jpg"}
                            fill
                            sizes="(max-width: 768px) 100vw, 480px"
                            style={{objectFit: 'cover'}}
                            placeholder='blur'
                            blurDataURL={s3Static + "placeholder.png"}
                            alt='Cool air from a freshly serviced aircon in a Singapore home'
                        />
                    </div>
                </div>
            </Section>

            <Section
                id='price-per-unit'
                title='Aircon servicing price per unit'
                intro='Servicing gets cheaper per unit when several units are done in one visit, because travel, parking and setup are shared. StringsSG general servicing for wall-mounted units:'
            >
                <DataTable
                    caption='StringsSG general aircon servicing price per unit by number of units'
                    columns={[ 'Units', 'You pay', 'Per unit', `Excl. ${p('transport')} transport` ]}
                    highlight={3}
                    rows={generalTiers.map((tier) => [ unitLabel(tier.units), money(tier.total), money(tier.perUnit), money(tier.effective) ])}
                />
                <div className='flex gap-3 rounded-2xl border border-orange-100 bg-orange-50 p-4 text-sm leading-6 text-gray-700'>
                    <Tag className='mt-0.5 h-5 w-5 shrink-0 text-orange-600' aria-hidden='true' />
                    <p>
                        <strong>Where &quot;from {p('effective-headline')} per unit&quot; comes from:</strong> most Singapore providers quote
                        a per-unit price and add a transport fee of around {p('transport')}. Our prices include transport, so the last column
                        sets that fee aside to compare like with like. For 5 units that is {p('effective-5')} per unit; the amount you
                        actually pay is {p('general-5')}.
                    </p>
                </div>
            </Section>

            <div id='full-price-list' className='scroll-mt-20 bg-gray-50 px-2 py-4'>
                <AirconPricing
                    standardServicing={standardServicing}
                    annualServicing={annualServicing}
                    chemicalServicing={chemicalServicing}
                    overhaulServicing={overhaulServicing}
                    gasTopup={gasTopup}
                    currencyCd={currencyCd}
                />
            </div>

            {otherPrices.length > 0 &&
                <Section id='other-aircon-prices' title='Other aircon service prices' intro='Ceiling cassette servicing, gas top-up and outdoor condenser cleaning, per unit with transport included.'>
                    <DataTable
                        caption='StringsSG prices for ceiling cassette servicing, gas top-up and condenser cleaning'
                        columns={[ 'Service', 'StringsSG', 'Typical Singapore price' ]}
                        highlight={1}
                        rows={otherPrices.map((row) => [
                            <Link key={row.service} href={row.href} prefetch={false} className={inlineLink}>{row.service}</Link>,
                            money(row.price),
                            row.market,
                        ])}
                    />
                </Section>
            }

            <Section
                id='worked-example'
                tinted
                title='Worked example: a 4-room HDB flat with 3 aircons'
                intro='A typical 4-room HDB flat has 3 wall-mounted units, one in the living room and one in each of two bedrooms. Serviced every 3 months, the yearly cost looks like this:'
            >
                <div className='grid md:grid-cols-3 gap-4'>
                    <div className='rounded-2xl border border-gray-200 bg-white p-5 shadow-sm'>
                        <p className='text-sm font-medium text-gray-500'>4 one-time bookings</p>
                        <p className='mt-2 text-3xl font-bold text-gray-900'>{p('oneoff4x-3')}</p>
                        <p className='mt-1 text-sm text-gray-500'>a year, at {p('general-3')} per visit</p>
                    </div>
                    <div className='relative rounded-2xl border-2 border-orange-500 bg-white p-5 shadow-sm'>
                        <span className='absolute -top-3 left-5 rounded-full bg-orange-600 px-3 py-0.5 text-xs font-semibold text-white'>
                            Save {p('annualSaving-3')}
                        </span>
                        <p className='text-sm font-medium text-gray-500'>Annual package, 4 visits</p>
                        <p className='mt-2 text-3xl font-bold text-orange-600'>{p('annual4x-3')}</p>
                        <p className='mt-1 text-sm text-gray-500'>a year, visits scheduled for you</p>
                    </div>
                    <div className='rounded-2xl border border-gray-200 bg-white p-5 shadow-sm'>
                        <p className='text-sm font-medium text-gray-500'>Annual package, 3 visits</p>
                        <p className='mt-2 text-3xl font-bold text-gray-900'>{p('annual3x-3')}</p>
                        <p className='mt-1 text-sm text-gray-500'>a year, every 4 months</p>
                    </div>
                </div>
                <p className='leading-8'>
                    If one unit starts smelling musty or cooling poorly, add a chemical wash for that unit only ({p('wash-1')}) rather
                    than washing all three. See the{' '}
                    <Link href='/sg/aircon-servicing-singapore/annual-package' prefetch={false} className={inlineLink}>annual servicing package</Link>
                    {' '}for what each visit covers.
                </p>
            </Section>

            <Section id='wash-vs-overhaul' title='Chemical wash vs chemical overhaul: which should you pay for?'>
                <div className='grid md:grid-cols-2 gap-4'>
                    <div className='rounded-2xl border border-gray-200 bg-white p-5 shadow-sm space-y-3'>
                        <div className='flex items-baseline justify-between gap-3'>
                            <h3 className='text-lg font-semibold text-gray-900'>Chemical wash</h3>
                            <p className='text-sm text-gray-500'>from <span className='text-xl font-bold text-orange-600'>{p('wash-1')}</span></p>
                        </div>
                        <p className='text-sm text-gray-600 leading-6'>
                            The unit is partly dismantled and the evaporator coil and water tray are treated with a chemical solution.
                        </p>
                        <ul className='space-y-2 text-sm text-gray-700'>
                            {[ 'Airflow is weak', 'The unit smells musty', 'It has not been serviced for a year or more' ].map((t) => (
                                <li key={t} className='flex gap-2'><CircleCheck className='mt-0.5 h-4 w-4 shrink-0 text-green-600' aria-hidden='true' />{t}</li>
                            ))}
                        </ul>
                    </div>
                    <div className='rounded-2xl border border-gray-200 bg-white p-5 shadow-sm space-y-3'>
                        <div className='flex items-baseline justify-between gap-3'>
                            <h3 className='text-lg font-semibold text-gray-900'>Chemical overhaul</h3>
                            <p className='text-sm text-gray-500'>from <span className='text-xl font-bold text-orange-600'>{p('overhaul-1')}</span></p>
                        </div>
                        <p className='text-sm text-gray-600 leading-6'>
                            The fan coil unit comes off the wall so the blower wheel, coil and full drainage line can be cleaned, then it
                            is reassembled and tested.
                        </p>
                        <ul className='space-y-2 text-sm text-gray-700'>
                            {[ 'Cooling is still poor after a recent service', 'The unit leaks water', 'It has been unused for months' ].map((t) => (
                                <li key={t} className='flex gap-2'><CircleCheck className='mt-0.5 h-4 w-4 shrink-0 text-green-600' aria-hidden='true' />{t}</li>
                            ))}
                        </ul>
                    </div>
                </div>
                <p className='leading-8'>
                    Neither replaces regular general servicing. See{' '}
                    <Link href='/sg/aircon-servicing-singapore/aircon-chemical-wash-and-overhaul' prefetch={false} className={inlineLink}>aircon chemical wash and overhaul</Link>
                    {' '}for what each job includes.
                </p>
            </Section>

            <Section
                id='gas-top-up-price'
                tinted
                title='Aircon gas top-up price by refrigerant'
                intro='Gas top-up prices depend on the refrigerant your unit uses and how much is needed. Typical Singapore prices:'
            >
                <div className='grid grid-cols-3 gap-3'>
                    {gasTopup.map((g) => (
                        <div key={g.type} className='rounded-2xl border border-gray-200 bg-white p-4 text-center shadow-sm'>
                            <p className='text-sm font-semibold text-gray-500'>{g.type}</p>
                            <p className='mt-1 text-base md:text-xl font-bold text-gray-900'>{g.priceRange.replace(/S\$/g, '$').replace(' - ', ' to ')}</p>
                        </div>
                    ))}
                </div>
                {p('gas-half') && p('gas-full') &&
                    <p className='leading-8'>
                        R32, used in most newer inverter units, costs the most. StringsSG charges <strong>{p('gas-half')}</strong> for a
                        half-tank top-up and <strong>{p('gas-full')}</strong> for a full tank per unit, transport included. Low gas almost
                        always means a leak, so have the leak found and fixed first, or the new gas escapes too. More on{' '}
                        <Link href='/sg/aircon-servicing-singapore/aircon-gas-top-up' prefetch={false} className={inlineLink}>aircon gas top-up</Link>.
                    </p>
                }
            </Section>

            <section id='cost-calculator' aria-labelledby='cost-calculator-title' className='scroll-mt-20 bg-slate-700 py-12'>
                <div className='max-w-2xl mx-auto px-4 pb-6 text-center text-white space-y-3'>
                    <Calculator className='mx-auto h-8 w-8 text-orange-400' aria-hidden='true' />
                    <h2 id='cost-calculator-title' className='text-2xl md:text-3xl font-bold'>Aircon servicing cost calculator</h2>
                    <p className='leading-7 text-white/80'>Choose the service and number of units to see what you should expect to pay in Singapore.</p>
                </div>
                <AirconServiceCostCalculator requestCategorySettings={requestCategorySettings}/>
            </section>

            <Section
                id='price-statistics'
                title='Aircon servicing price statistics and trends'
                intro={`Based on ${DATA_POINTS} aircon price data points from service requests and quotes on StringsSG since 2018.`}
            >
                <div className='grid md:grid-cols-2 gap-4'>
                    <ChartCard
                        title='What Singapore customers pay for 3 units'
                        takeaway={`Share of customers by price paid for a one-time general service of 3 units. The most common price is ${bucketLabel(peakBucket.range)} (${peakBucket.cost}%). ${shareAbove90}% pay more than $90, often without comparing all-in prices.`}
                    >
                        <CostDistributionChart data={costData} />
                    </ChartCard>
                    <ChartCard
                        title={`Aircon servicing demand, ${demandFirstYear} to ${demandLastYear}`}
                        takeaway={`Demand is highest around ${monthNames[peakMonth]} and lowest around ${monthNames[lowMonth]}. The ${monthNames[peakMonth]} peak has grown from ${peakPoints[0].demand} to ${peakPoints[peakPoints.length - 1].demand} on our demand index, so book early for mid-year slots.`}
                    >
                        <DemandChart data={demandData} />
                    </ChartCard>
                    <div className='md:col-span-2'>
                        <ChartCard
                            title='Average price to service 1 aircon unit in Singapore'
                            takeaway={`The average market price for 1 unit rose from $${trendFirst.price} in ${trendFirst.year} to $${trendLast.price} in ${trendLast.year} (+${trendRise}%), with higher labour, transport and materials costs.${oneUnitPrice < trendLast.price ? ` StringsSG's 1-unit price of ${money(oneUnitPrice)} is below the ${trendLast.year} average.` : ''}`}
                        >
                            <PriceTrendChart data={priceTrendData} />
                        </ChartCard>
                    </div>
                </div>
            </Section>

            <Section id='price-factors' tinted title='What affects the price of aircon servicing?'>
                <div className='grid md:grid-cols-2 gap-4'>
                    <IconCard icon={Snowflake} title='Number of units'>
                        The fixed cost of a visit is shared across every unit serviced, so 5 units cost {p('perUnit-5')} each against{' '}
                        {p('general-1')} for a single unit. Service them all in one visit.
                    </IconCard>
                    <IconCard icon={Building2} title='Type and size of unit'>
                        Prices here are for wall-mounted units. Ceiling cassette units take longer to open and clean, so they cost more -
                        see <Link href='/sg/aircon-servicing-singapore/ceiling-cassette-aircon-service' prefetch={false} className={inlineLink}>ceiling cassette aircon servicing</Link>.
                        Larger units (18,000 BTU and above) can cost more to overhaul.
                    </IconCard>
                    <IconCard icon={Wrench} title='Condition of the unit'>
                        A unit left a year or more without servicing may need a chemical wash instead of a general service. If it is not
                        cooling or keeps tripping, it needs diagnosis first - see{' '}
                        <Link href='/sg/aircon-servicing-singapore/aircon-repair' prefetch={false} className={inlineLink}>aircon repair</Link>.
                    </IconCard>
                    <IconCard icon={MapPin} title='Location and parking'>
                        A service van needs parking for 30 minutes to 2 hours. Where parking is paid, such as the CBD, condos without
                        visitor lots and commercial buildings, some providers pass that cost on.
                    </IconCard>
                    <IconCard icon={Receipt} title='GST'>
                        A GST-registered company must charge 9% GST. Check whether a quote includes it, or the final bill will be 9%
                        higher than the headline price.
                    </IconCard>
                    <IconCard icon={BadgePercent} title='Brand and advertising'>
                        Established brands tend to charge more, and companies that rely heavily on paid search ads often price
                        higher to cover their ad spend. A higher price is not always better service.
                    </IconCard>
                </div>
            </Section>

            <Section id='hidden-fees' title='Hidden fees to check before you book' intro='A low headline price is not always the cheapest quote. Ask whether these are included before comparing:'>
                <div className='grid md:grid-cols-2 gap-4'>
                    <div className='rounded-2xl border border-gray-200 bg-white p-5 shadow-sm'>
                        <h3 className='mb-3 font-semibold text-gray-900'>Watch out for</h3>
                        <ul className='space-y-3 text-sm text-gray-700 leading-6'>
                            {[
                                [ 'Transport or call-out fee', 'often $10 to $30 per visit on top of the per-unit price.' ],
                                [ 'GST', 'a GST-registered company adds 9% if its quote is before tax.' ],
                                [ 'Minimum units', 'a low per-unit price may only apply from 3 units.' ],
                                [ 'Upselling on site', 'a general service turned into a chemical wash or gas top-up after arrival. Ask for the reason and photos.' ],
                                [ 'Surcharges', 'for weekends and public holidays.' ],
                            ].map(([ label, text ]) => (
                                <li key={label} className='flex gap-2'>
                                    <TriangleAlert className='mt-0.5 h-4 w-4 shrink-0 text-amber-500' aria-hidden='true' />
                                    <span><strong>{label}:</strong> {text}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div className='rounded-2xl border border-green-200 bg-green-50 p-5'>
                        <h3 className='mb-3 flex items-center gap-2 font-semibold text-gray-900'>
                            <ShieldCheck className='h-5 w-5 text-green-600' aria-hidden='true' /> With StringsSG
                        </h3>
                        <ul className='space-y-3 text-sm text-gray-700 leading-6'>
                            {[
                                'Transport included in every price',
                                'No minimum booking fee',
                                'The full price is shown before you confirm',
                                'Add-on prices, such as condenser cleaning and gas top-up, are listed on this page',
                            ].map((t) => (
                                <li key={t} className='flex gap-2'>
                                    <CircleCheck className='mt-0.5 h-4 w-4 shrink-0 text-green-600' aria-hidden='true' />{t}
                                </li>
                            ))}
                        </ul>
                        <p className='mt-3 text-sm'>
                            <Link href='/sg/aircon-servicing-singapore/aircon-condenser-cleaning' prefetch={false} className={inlineLink}>Condenser cleaning</Link>
                            {' '}is the most common add-on for units that still run warm after servicing.
                        </p>
                    </div>
                </div>
            </Section>

            <Section id='save-money' tinted title='How to get the cheapest aircon servicing in Singapore'>
                <ol className='grid md:grid-cols-2 gap-4'>
                    {[
                        [ 'Service every unit in one visit', `The price per unit drops from ${p('general-1')} for 1 unit to ${p('perUnit-5')} each for 5.` ],
                        [ 'Take an annual package', `For 3 units, 4 visits a year cost ${p('annual4x-3')} instead of ${p('oneoff4x-3')} booked one by one.` ],
                        [ 'Compare all-in prices', 'Add transport, GST and any minimum-unit rule to each quote before comparing headline prices.' ],
                        [ 'Book outside the peak', `Demand peaks around ${monthNames[peakMonth]}. Booking earlier in the year makes slots and promotions easier to get.` ],
                        [ 'Clean the filters yourself', 'Rinsing the air filters every 2 to 4 weeks keeps airflow up and delays the need for a chemical wash.' ],
                        [ 'Do not skip servicing', `A neglected unit often needs a chemical overhaul at ${p('overhaul-1')} instead of a ${p('general-1')} general service.` ],
                    ].map(([ title, text ], i) => (
                        <li key={title} className='flex gap-4 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm'>
                            <span className='flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-orange-600 text-sm font-bold text-white'>{i + 1}</span>
                            <div>
                                <h3 className='font-semibold text-gray-900'>{title}</h3>
                                <p className='mt-1 text-sm text-gray-600 leading-6'>{text}</p>
                            </div>
                        </li>
                    ))}
                </ol>
                <div className='grid md:grid-cols-2 gap-8 items-center rounded-2xl border border-gray-200 bg-white p-5 md:p-8 shadow-sm'>
                    <div className='space-y-3 leading-7'>
                        <h3 className='flex items-center gap-2 text-lg font-semibold text-gray-900'>
                            <Database className='h-5 w-5 text-orange-600' aria-hidden='true' /> How StringsSG keeps prices low
                        </h3>
                        <p className='text-gray-600'>
                            We set fixed aircon servicing prices from {DATA_POINTS} price data points collected since 2018, so you get a fair
                            market price without calling around for quotes.
                        </p>
                        <p className='text-gray-600'>
                            When you book on our website or app, the system assigns a vetted aircon contractor near you who can do the job
                            at that price. You see the total before you confirm, and nothing is added afterwards.
                        </p>
                    </div>
                    <div className='relative h-64 md:h-80 w-full overflow-hidden rounded-2xl'>
                        <Image
                            src={s3Static + "blog/living/girl-saved-money-on-service.png"}
                            fill
                            sizes="(max-width: 768px) 100vw, 420px"
                            style={{objectFit: 'cover'}}
                            placeholder='blur'
                            blurDataURL={s3Static + "placeholder.png"}
                            alt='Customer who saved money on aircon servicing in Singapore'
                        />
                    </div>
                </div>
            </Section>

            <section className='bg-linear-to-tr from-orange-600 to-amber-500 py-12'>
                <div className='max-w-3xl mx-auto px-4 text-center text-white space-y-4'>
                    <h2 className='text-2xl md:text-3xl font-bold'>See your exact aircon servicing price</h2>
                    <p className='text-white/90 leading-7'>
                        Pick your service and number of units. The total, with transport included, is shown before you confirm.
                    </p>
                    <Link
                        href="/sg/aircon-servicing-singapore"
                        className='inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 font-semibold text-orange-600 shadow-sm hover:bg-orange-50'
                    >
                        Book aircon servicing <ArrowRight className='h-4 w-4' aria-hidden='true' />
                    </Link>
                </div>
            </section>

            <div id='faq' className='scroll-mt-20'>
                <Faq title="Aircon servicing price FAQs" questionsAnswers={faqs} showSearch={false} />
            </div>
            <BlogsList blogs={airconBlogs}/>
        </div>
        {isShowJobScope &&
            <AnimatePresence>
                <AirconScope
                setIsShowJobScope={setIsShowJobScope}
                serviceType={serviceType}
                unit={unit}
                unitType={unitType}
                cost={cost} />
            </AnimatePresence>
        }
        <Script
          id="google-tagmanager-new"
          src="https://www.googletagmanager.com/gtag/js?id=G-QD1HGGHDYJ"
          strategy="lazyOnload"
          onLoad={() => {
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());

            gtag('config', 'G-QD1HGGHDYJ');
          }}
      />
      <Script id="facebook-tracking" strategy="lazyOnload">
          {`!function(f,b,e,v,n,t,s)
					  {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
					  n.callMethod.apply(n,arguments):n.queue.push(arguments)};
					  if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
					  n.queue=[];t=b.createElement(e);t.async=!0;
					  t.src=v;s=b.getElementsByTagName(e)[0];
					  s.parentNode.insertBefore(t,s)}(window, document,'script',
					  'https://connect.facebook.net/en_US/fbevents.js');
					  fbq('init', '128634041116395');
					  fbq('track', 'PageView');`}
        </Script>
    </>
  )
}
